import { KPICard } from "@/components/accounting/kpi-card";
import {
	CommerceAreaChart,
	CommerceBarChart,
	CommerceLineChart,
	toDailyChartData,
} from "@/components/commerce";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import { useOrderAnalytics } from "@/hooks/use-commerce";
import { formatCurrency, formatDate } from "@/lib/format";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/commerce/analytics")({
	component: AnalyticsPage,
});

function AnalyticsPage() {
	const { t } = useTranslation("commerce");
	const qc = useQueryClient();
	const [days, setDays] = useState<7 | 30 | 90>(30);

	useBreadcrumbs([
		{ label: t("analytics", { defaultValue: "Аналитика" }) },
	]);

	const statusBar = useMemo<StatusBarItem[]>(() => [], []);
	useWorkspacePage({ statusBar });

	const { data: analytics, isLoading } = useOrderAnalytics(days);

	const summary = analytics?.summary;
	const dailyStats = analytics?.dailyStats ?? [];
	const chartData = useMemo(
		() => toDailyChartData(dailyStats as any[]),
		[dailyStats],
	);
	const isEmpty = !isLoading && dailyStats.length === 0;

	return (
		<div className="flex flex-col h-full overflow-hidden">
			<div className="flex items-center gap-2 px-4 py-2 shrink-0 justify-end">
				{[7, 30, 90].map((p) => (
					<Button
						key={p}
						variant={days === p ? "default" : "outline"}
						size="sm"
						className="h-9"
						onClick={() => setDays(p as 7 | 30 | 90)}
					>
						{`${p}д`}
					</Button>
				))}
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() =>
						qc.invalidateQueries({
							queryKey: ["commerce", "order-analytics", days],
						})
					}
				>
					{t("menu_refresh", { defaultValue: "Обновить" })}
				</Button>
			</div>
			{/* ── KPI strip ── */}
			<div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border-b shrink-0">
				{isLoading ? (
					Array.from({ length: 4 }).map((_, i) => (
						<Skeleton key={`kpi-${i}`} className="h-[72px]" />
					))
				) : (
					<>
						<KPICard
							label={t("total_orders_kpi", {
								defaultValue: "Заказов",
							})}
							amount={String(summary?.totalOrders ?? 0)}
						/>
						<KPICard
							label={t("revenue_kpi", {
								defaultValue: "Выручка",
							})}
							amount={String(
								Number(summary?.totalRevenueCents ?? 0) / 100,
							)}
						/>
						<KPICard
							label={t("avg_order_kpi", {
								defaultValue: "Средний чек",
							})}
							amount={String(
								Number(summary?.avgOrderValueCents ?? 0) / 100,
							)}
						/>
						<KPICard
							label={t("avg_fulfillment_kpi", {
								defaultValue: "Ср. время обработки",
							})}
							amount={
								summary?.avgFulfillmentHours
									? `${Number(summary.avgFulfillmentHours).toFixed(1)} ч`
									: "—"
							}
						/>
					</>
				)}
			</div>

			{/* ── Content ── */}
			<div className="flex-1 overflow-auto">
				{isEmpty && (
					<div className="flex items-center justify-center h-full">
						<p className="text-sm text-muted-foreground">
							{t("analytics_empty", {
								defaultValue:
									"Нет данных за выбранный период",
							})}
						</p>
					</div>
				)}

				{!isEmpty && !isLoading && (
					<div className="p-4 space-y-4">
						{/* Charts 2x2 */}
						<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
							<CommerceAreaChart
								title={t("revenue_kpi", {
									defaultValue: "Выручка",
								})}
								data={chartData}
								dataKey="revenue"
								tooltipLabel={t("revenue_kpi", {
									defaultValue: "Выручка",
								})}
								currency
							/>
							<CommerceBarChart
								title={t("total_orders_kpi", {
									defaultValue: "Заказов",
								})}
								data={chartData}
								dataKey="orders"
								tooltipLabel={t("total_orders_kpi", {
									defaultValue: "Заказов",
								})}
							/>
							<CommerceLineChart
								title={t("avg_order_kpi", {
									defaultValue: "Средний чек",
								})}
								data={chartData}
								dataKey="aov"
								tooltipLabel={t("avg_order_kpi", {
									defaultValue: "Средний чек",
								})}
								currency
							/>
							<CommerceBarChart
								title={t("customers_col", {
									defaultValue: "Клиенты",
								})}
								data={chartData}
								dataKey="customers"
								tooltipLabel={t("customers_col", {
									defaultValue: "Клиенты",
								})}
								color="hsl(142.1 76.2% 36.3%)"
							/>
						</div>

						{/* Daily stats table */}
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>
										{t("date_col", {
											defaultValue: "Дата",
										})}
									</TableHead>
									<TableHead className="text-right">
										{t("orders_col", {
											defaultValue: "Заказы",
										})}
									</TableHead>
									<TableHead className="text-right">
										{t("revenue_col", {
											defaultValue: "Выручка",
										})}
									</TableHead>
									<TableHead className="text-right">
										{t("aov_col", {
											defaultValue: "Средний чек",
										})}
									</TableHead>
									<TableHead className="text-right">
										{t("customers_col", {
											defaultValue: "Клиенты",
										})}
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{dailyStats.map((row) => (
									<TableRow key={row.date}>
										<TableCell>
											{formatDate(
												new Date(row.date),
												"d MMM yyyy",
											)}
										</TableCell>
										<TableCell className="text-right tabular-nums">
											{row.orderCount}
										</TableCell>
										<TableCell className="text-right tabular-nums">
											{formatCurrency(
												Number(row.revenueCents),
											)}
										</TableCell>
										<TableCell className="text-right tabular-nums">
											{formatCurrency(
												Number(row.avgOrderValueCents),
											)}
										</TableCell>
										<TableCell className="text-right tabular-nums">
											{row.uniqueCustomers}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				)}
			</div>
		</div>
	);
}
